import { ComparatorEnum } from "src/app/enums/enums";
import { CategoryNode, CompanyWithRolesAndWorkCentersDTO, DataRequest, RoleDTO, UserDTO } from "src/app/interfaces/base-entity";
import { pkcs5, random, cipher, util } from 'node-forge'
import { toZonedTime, format } from 'date-fns-tz';


export function fillFiltersAndSorts(event: any, dataRequest: DataRequest) {
    const rawFilters = event?.filters;
    if (rawFilters && typeof rawFilters === 'object') {
      Object.keys(rawFilters).forEach((key) => {
        const filterMeta = rawFilters[key];
        const metas = Array.isArray(filterMeta) ? filterMeta : [filterMeta];

        metas.forEach((filter) => {
          if (filter == null || filter.value === null || filter.value === undefined || filter.value === '') {
            return;
          }

          const matchMode = typeof filter.matchMode === 'string' ? filter.matchMode : '';
          let value = filter.value;
          if (matchMode.includes('date')) {
            value = new Date(filter.value).toISOString();
          }

          const comparator = ComparatorEnum[matchMode as keyof typeof ComparatorEnum];
          if (comparator === undefined) {
            return;
          }

          dataRequest.filters.push({
            field: key,
            value: String(value),
            comparator,
          });
        });
      });
    }

    if (event?.sortField) {
      dataRequest.sorts = [{ field: `${event.sortField}`, sortDirection: event.sortOrder! }];
    }
  }

  export class Util {

    public static encriptString(data: string, iterators: number = 1000): string {
      const salt = random.getBytesSync(16);
      const iv = random.getBytesSync(16);
      const key = pkcs5.pbkdf2('Sicop App 2Bd345$32As', salt, iterators, 32);
      const ciphr = cipher.createCipher('AES-CBC', key);
      ciphr.start({ iv });
      ciphr.update(util.createBuffer(data, 'utf8'));
      ciphr.finish();
      const encrypted = ciphr.output.getBytes();
      // Concatena como buffer, no como string
      const resultBuffer = util.createBuffer();
      resultBuffer.putBytes(salt);
      resultBuffer.putBytes(iv);
      resultBuffer.putBytes(encrypted);
      return util.encode64(resultBuffer.getBytes());
        //return data;
    }

    public static decriptString(data: string, iterators: number = 1000): string {
         const encrypted = util.decode64(data);
          const buffer = util.createBuffer(encrypted);
          const salt = buffer.getBytes(16);
          const iv = buffer.getBytes(16);
          const ciphertext = buffer.getBytes();
          const key = pkcs5.pbkdf2('Sicop App 2Bd345$32As', salt, iterators, 32);
          const decipher = cipher.createDecipher('AES-CBC', key);
          decipher.start({ iv });
          decipher.update(util.createBuffer(ciphertext));
          decipher.finish();
          // Usa .toString('utf8') si tu librería lo soporta, si no, usa .data
          return decipher.output.toString();
        //return data;
    }


    public static sanityJSON(json: string, key?: string): string {
        if (key === undefined) {
            const char = json.slice(0, 1);
            if (char === '[') {
                key = ']';
            } else {
                key = '}';
            }
        }
        const aJson = json.split(key);
        if (aJson.length > 1) {
            const abJson = aJson[aJson.length - 1].split(']');
            if (abJson.length > 1) {
                aJson[aJson.length - 1] = this.sanityJSON(aJson[aJson.length - 1], ']');
            } else {
                aJson[aJson.length - 1] = '';
            }
        }
        return aJson.join(key);
    }
}  

export function formatDateToDateOnly(dateToApply: any): string {
  if (!dateToApply) {
    return '';
  }
  const date = new Date(dateToApply);
  return date.toISOString().split('T')[0];
}

export function parseDateString(dateString: string): Date | null {
  if (!dateString) {
    return null;
  }
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function checkStandardFromRoute(router: any, route: any): boolean {
  const url = router.url;
  const routePath = route.snapshot.url.join('/');
  const fullPath = route.snapshot.routeConfig?.path || '';
  
  const isStandard0312 = url.includes('standard-0312') || 
                        routePath.includes('standard-0312') || 
                        fullPath.includes('standard-0312');
  
  const isISO = url.includes('iso-45001') || 
               routePath.includes('iso-45001') || 
               fullPath.includes('iso-45001') ||
               url.includes('iso45001') || 
               routePath.includes('iso45001') || 
               fullPath.includes('iso45001');
  
  return isISO && !isStandard0312;
}

/**
 * Formatea una fecha a string legible, con opción de incluir la hora.
 * @param date Fecha a formatear (Date | string | number)
 * @param showTime Si es true, incluye la hora (HH:mm). Por defecto false.
 * @param locale Idioma para el formato, por defecto 'es-CO'.
 * @returns Fecha formateada como string.
 */
export function formatDateTime(date: Date | string | number, showTime: boolean = false, locale: string = 'es-CO'): string {
    if (!date) return '';
    let d: Date;
    if (date instanceof Date) {
        d = date;
    } else {
        d = new Date(date);
    }
    if (isNaN(d.getTime())) return '';
    
    // Usar date-fns-tz para formatear con zona horaria de Colombia
    const timeZone = 'America/Bogota';
    const options: Intl.DateTimeFormatOptions = showTime
        ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone }
        : { year: 'numeric', month: '2-digit', day: '2-digit', timeZone };
    return d.toLocaleString(locale, options);
}

/**
 * Formatea una fecha usando la misma lógica que el pipe localDate para consistencia.
 * Convierte fechas UTC a la zona horaria de Colombia y las formatea correctamente.
 * @param value Fecha a formatear (string | Date)
 * @param timeZone Zona horaria de destino (por defecto 'America/Bogota')
 * @param dateFormat Formato de fecha (por defecto 'dd/MM/yyyy HH:mm')
 * @returns Fecha formateada como string
 */
export function formatLocalDate(value: string | Date, timeZone: string = 'America/Bogota', dateFormat: string = 'dd/MM/yyyy HH:mm'): string {
    if (!value) {
        return '';
    }

    try {
        // Import estático - usar mismo enfoque que el pipe localDate
        const { toZonedTime, format } = require('date-fns-tz');
        
        // Normalizar el valor a string con 'Z' para asegurar interpretación UTC
        let dateString: string;
        
        if (value instanceof Date) {
            // Si es Date, convertir a ISO string para preservar UTC
            dateString = value.toISOString();
        } else if (typeof value === 'string') {
            // Si es string, asegurar que tenga 'Z' al final para indicar UTC
            // Esto es crítico: sin 'Z', JavaScript interpreta como hora local
            dateString = value.endsWith('Z') ? value : `${value}Z`;
        } else {
            // Fallback: convertir a Date y luego a ISO string
            dateString = new Date(value).toISOString();
        }
        
        // Crear Date object desde string UTC
        // new Date() con string que termina en 'Z' siempre interpreta como UTC
        const utcDate = new Date(dateString);
        
        // Validar que la fecha sea válida
        if (isNaN(utcDate.getTime())) {
            console.error('Invalid date:', value, 'Normalized to:', dateString);
            return '';
        }
        
        // 1. Convert the incoming date from UTC to the target time zone
        // toZonedTime requiere que el Date represente UTC (por eso usamos string con 'Z')
        const zonedDate = toZonedTime(utcDate, timeZone);
        
        // 2. Format the resulting date
        const formatted = format(zonedDate, dateFormat, { timeZone });
        return formatted;
    } catch (error) {
        // Fallback for invalid dates
        return value.toString();
    }
}

 export function mapCategoryNodeToTreeNode(nodes: any[], icon = 'pi pi-fw pi-sitemap'): any[] {
        return nodes.map(node => ({
          icon: icon,
          key: String(node.id),
          label: node.name, 
          data: {...node, icon: icon},
          description: node.description,       
          children: node.children && node.children.length > 0 ? mapCategoryNodeToTreeNode(node.children) : []
        }));
  }


  
      /**
       * Transforma los datos del backend al formato esperado por el frontend
       */
      export function transformBackendDataToCompanyList(user: UserDTO): CompanyWithRolesAndWorkCentersDTO[] {
          const companyList: CompanyWithRolesAndWorkCentersDTO[] = [];
          
          if (user.companyList && user.companyList.length > 0) {
              return user.companyList;
          }
          
          if (user.managementTree && user.managementTree.length > 0) {
              for (const company of user.managementTree) {
                  const companyWithRoles: CompanyWithRolesAndWorkCentersDTO = {
                      companyId: company.id,
                      companyName: company.name,
                      economicGroup: company.economicGroup,
                      logoUrl: company.logoUrl,
                      roles: getRolesFromUserRoleList(user.userRoleList || [], company.id)
                  };
                  companyList.push(companyWithRoles);
              }
          }
          
          if (user.employmentTree && user.employmentTree.length > 0) {
              for (const company of user.employmentTree) {
                  const existingCompany = companyList.find(c => c.companyId === company.id);
                  if (existingCompany) {
                      const additionalRoles = getRolesFromUserRoleList(user.userRoleList || [], company.id);
                      existingCompany.roles = [...existingCompany.roles, ...additionalRoles];
                  } else {
                      const companyWithRoles: CompanyWithRolesAndWorkCentersDTO = {
                          companyId: company.id,
                          companyName: company.name,
                          economicGroup: company.economicGroup,
                          logoUrl: company.logoUrl,
                          roles: getRolesFromUserRoleList(user.userRoleList || [], company.id)
                      };
                      companyList.push(companyWithRoles);
                  }
              }
          }
          
          return companyList;
      }

     function getRolesFromUserRoleList(userRoleList: any[], companyId: number): RoleDTO[] {
              if (!userRoleList || userRoleList.length === 0) {
                  return [];
              }
              
              const roles: RoleDTO[] = [];
              
              for (const userRole of userRoleList) {
                  if (userRole.companyId && userRole.companyId === companyId) {
                      roles.push({
                          roleId: userRole.roleId,
                          roleName: getRoleNameInSpanish(userRole.roleName),
                          description: getRoleNameInSpanish(userRole.roleName),
                          workCenters: userRole.workCenters || []
                      });
                  }
                  else if (!userRole.companyId) {
                      roles.push({
                          roleId: userRole.roleId,
                          roleName: getRoleNameInSpanish(userRole.roleName),
                          description: getRoleNameInSpanish(userRole.roleName),
                          workCenters: userRole.workCenters || []
                      });
                  }
              }
              
              return roles;
         }

     function getRoleNameInSpanish(roleName: string): string {
        const roleTranslations: { [key: string]: string } = {
            'Employee': 'Empleado',
            'AdminWorkCenter': 'Administrador Centro de Trabajo',
            'Root': 'Administrador Sistema',
            'AdminCompany': 'Administrador Compañía'
        };
        
        return roleTranslations[roleName] || roleName;
    }
